/**
 * fleet_dock.ts — the STATION DOCK window (P in space, near a station):
 * Shipyard (buy hulls) · Hangar (activate/rename) · Loadout (equip the
 * comp_/droid_ playerItems into the active ship's ten hardpoints).
 *
 * Pure mirror UI (the client-mirrors law): every button is a reducer call
 * and the panel re-renders from the replicated player_ship / ship_component /
 * pilot_state / player_item / wallet rows on a 1 s tick — it never trusts
 * its own click side-effects.
 */
import { atlas } from '../state.js';
import { CHASSIS, DEFAULT_CHASSIS, SLOTS, SLOT_LABEL, slotOfItem, shipStats, type FleetSlot } from './fleet.js';
import { FIT_BUDGET, fitCost, sysTierOf, marketEpoch, HOLD_UNITS_PER_CARGO } from '@shared/space_industry.js';
import { EXCHANGE_GOODS, NOT_ON_EXCHANGE, commodityOfFamily, exchangeUnitPrice, orderQuote, lossPreview, insuranceQuote } from '@shared/space_catalog.js';
import { summarizeAboard, scrapRefusal, type AboardLot } from '@shared/hull_custody.js';
import { sfx } from './sfx.js';
// space campaign B (2026-09-30): wardecs under the guild court.
import { Identity } from 'spacetimedb';
import { wardecRoute, WARDEC_FLAG, WARDEC_COURT_TITLES } from './wardec_rules.js';
import { GUILD_VAULT_BASE } from './guild_rules.js';
// space campaign E (2026-09-30): the port run's terms, relay and receipt chain live in their own window.
import { portWindow } from './port_window.js';
import { ensurePortRows, termsOf as portTermsOf, crewOf as portCrewOf, relayRole, statusOf as portStatusOf, cityName as portCityName } from './port_client.js';
import { dryLines } from './port_terms.js';
import { keyHint } from './keybinds.js';
import { renderFireGroups, fitRevision } from './space_fire_groups.js'; // space campaign I: the Loadout's fire groups
// space campaign J (2026-09-30): located markets — the comparison, the health card, the foundry, the courier terms, the
// loadout's quality tradeoff.
import { renderMarkets, renderEconomy, renderFoundry, type DockHost } from './market_panel.js';
import { renderStation } from './station_panel.js'; // space campaign K: the Station tab (roles, quoted repair, berth, muster, plans)
import { clientDepthUnits, clientRefineEff } from './station_client.js';
import { courierWindow } from './courier_window.js';
import { collateralCheck } from './courier_terms.js';
import { qualityTradeoff } from './quality_tradeoff.js';

type Tab = 'station' | 'yard' | 'hangar' | 'loadout' | 'duty' | 'exchange' | 'orders' | 'haulage' | 'kills' | 'wars' | 'skills' | 'abyss' | 'industry' | 'hold' | 'compare' | 'health';

/** Client mirror of server FLEET_DUTY / FLEET_ARC (display only — the server
 *  is the authority on rewards + gates). */
const DUTY_KINDS: Array<{ kind: string; label: string; brief: string; rewardSov: number; requiresRank?: number; requiresTrust?: number }> = [
  { kind: 'patrol', label: 'Patrol', brief: '3 kills at a home-cluster den', rewardSov: 60 },
  { kind: 'sweep', label: 'Sweep', brief: '5 kills at a belt den', rewardSov: 150 },
  { kind: 'strike', label: 'Strike', brief: '3 kills at a fortress — mints a battle token', rewardSov: 300 },
  // Standings-gated agents (the server re-checks every gate).
  { kind: 'vanguard', label: 'Vanguard', brief: '6 kills across the belts', rewardSov: 320, requiresRank: 2 },
  { kind: 'blockade', label: 'Blockade', brief: '5 fortress kills', rewardSov: 700, requiresRank: 3 },
  { kind: 'crucible', label: 'Crucible', brief: '8 fortress kills — the elite board', rewardSov: 1400, requiresRank: 4, requiresTrust: 60 },
];

// space campaign A (gap 20): the Exchange rows are EXCHANGE_GOODS (@shared/space_catalog) — the ONE projection of
// the server's commodity catalog; the hand list of nine (a "Luxuries" row the server refused, four wrong bases, no
// crystal / gas / noble ore) is gone.
const ARC_TITLES: Record<string, string[]> = {
  aurelian: ['Concord Oath', 'Keeper of Lanes', 'Shield of the Belt', 'Void Warden', 'Concord Paragon'],
  borean: ['Compact Pact', 'Icefield Runner', 'Gale of the Rim', 'Bulwark of Borea', 'Aurora Sovereign'],
};
/** Versona mission-givers: the civ's duty officer (flavor names, client-only). */
const FACTION_OFFICER: Record<string, string> = {
  aurelian: 'Warden-Captain Sel Amara, Aurelian Concord',
  borean: 'Iron-Pilot Veyra Osk, Borean Compact',
  '': 'The station duty officer',
};

const BTN = 'background:var(--ax-bg3);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);color:var(--ax-text);padding:3px 10px;cursor:pointer;font:11px var(--ax-mono)';

export class FleetDock {
  private root: HTMLDivElement | null = null;
  private body: HTMLDivElement | null = null;
  private pilotEl: HTMLDivElement | null = null;
  private tab: Tab = 'yard';
  private timer = 0;
  private status = '';
  /** space campaign A: the order form survives the 1 s re-render (its quote line reads it). */
  private orderForm = { fam: 'ferrite_ore', side: 'sell' as 'buy' | 'sell', px: '10', qty: '10' };
  /** space campaign J: the courier post form survives the re-render too (the Markets tab prefills it). */
  private courierForm = { dest: '', fam: 'grain', qty: '10', reward: '50', collateral: '100' };
  /** space campaign J: the located-market panels' handle on the dock (buttons, rows, tabs, the shared forms). */
  private host(): DockHost {
    return {
      button: (l, e, f) => this.button(l, e, f),
      row: (c) => this.row(c),
      noteLine: (t, c) => this.noteLine(t, c),
      stationCtx: () => this.stationCtx(),
      goTab: (t) => { this.tab = t as Tab; this.render(); },
      tradeLevel: () => this.tradeLevel(),
      orderForm: this.orderForm,
      courierForm: this.courierForm,
    };
  }
  private readonly onClose: () => void;
  private readonly stationCtx: () => { systemId: string; stationKey: string };
  private readonly esc = (e: KeyboardEvent): void => {
    if (e.code === 'Escape' || e.code === 'KeyP') {
      const t = e.target;
      if (t instanceof Element && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      e.stopPropagation();
      this.close();
    }
  };

  constructor(onClose: () => void, stationCtx?: () => { systemId: string; stationKey: string }) {
    this.onClose = onClose;
    this.stationCtx = stationCtx ?? (() => ({ systemId: 'sys0', stationKey: 'sys0:st0' }));
  }

  open(): void {
    if (this.root) return;
    const root = document.createElement('div');
    root.id = 'fleet-dock';
    root.style.cssText =
      'position:fixed;inset:0;z-index:39;display:flex;align-items:center;justify-content:center;' +
      'background:color-mix(in srgb, var(--ax-bg) 82%, transparent);backdrop-filter:blur(3px);font:12px var(--ax-font);color:var(--ax-text);pointer-events:auto';
    const panel = document.createElement('div');
    panel.style.cssText =
      'background:color-mix(in srgb, var(--ax-bg) 94%, transparent);border:1px solid var(--ax-line);border-radius:var(--ax-win-radius);padding:14px;' +
      // space campaign A: border-box + a viewport cap, so the dock fits 720p at 200 % UI scale (it was 670 px wide, fixed).
      'box-sizing:border-box;width:min(670px, calc(100vw - 16px));max-height:max(78vh, min(100vh - 24px, 420px));display:flex;flex-direction:column;gap:8px';
    const title = document.createElement('div');
    title.textContent = 'STATION DOCK';
    title.style.cssText = 'font:700 11px var(--ax-mono);color:var(--ax-muted);letter-spacing:1.4px';
    const pilot = document.createElement('div');
    pilot.style.cssText = 'font:11px var(--ax-mono);color:var(--ax-soft)';
    const tabs = document.createElement('div');
    tabs.style.cssText = 'display:flex;gap:6px;flex-wrap:wrap'; // thirteen tabs wrap rather than run off the panel
    for (const [key, label] of [['station', 'Station'], ['yard', 'Shipyard'], ['hangar', 'Hangar'], ['loadout', 'Loadout'], ['duty', 'Duty'], ['exchange', 'Exchange'], ['orders', 'Orders'], ['haulage', 'Haulage'], ['kills', 'Kills'], ['wars', 'Wars'], ['skills', 'Skills'], ['abyss', 'Abyss'], ['industry', 'Industry'], ['hold', 'Hold'], ['compare', 'Markets'], ['health', 'Economy']] as Array<[Tab, string]>) {
      const b = document.createElement('button');
      b.dataset.tab = key;
      b.textContent = label;
      b.style.cssText = this.tabCss(key === this.tab);
      b.onclick = () => { this.tab = key; this.render(); };
      tabs.appendChild(b);
    }
    const body = document.createElement('div');
    body.style.cssText = 'overflow-y:auto;display:flex;flex-direction:column;gap:6px;min-height:min(220px, 30vh)'; // space campaign A: a short screen scrolls the body instead of spilling past the panel
    const hint = document.createElement('div');
    hint.textContent = 'P / Esc — close';
    hint.style.cssText = 'color:var(--ax-muted);font:10px var(--ax-mono);text-align:center';
    panel.append(title, pilot, tabs, body, hint);
    root.appendChild(panel);
    document.body.appendChild(root);
    this.root = root;
    this.body = body;
    this.pilotEl = pilot;
    window.addEventListener('keydown', this.esc, true);
    // space campaign A: the periodic re-render rebuilt every input under the player's cursor (typed values and
    // focus lost within a second) — it waits while a dock field has focus; clicks still re-render at once.
    this.timer = window.setInterval(() => { if (!this.editing()) this.render(); }, 1000);
    this.render();
  }

  /** space campaign K: open on a named tab (the repair key lands on the Station tab's quoted job). */
  showTab(t: string): void { this.tab = t as Tab; this.render(); }

  close(): void {
    if (!this.root) return;
    window.removeEventListener('keydown', this.esc, true);
    window.clearInterval(this.timer);
    this.root.remove();
    this.root = null;
    this.body = null;
    this.onClose();
  }

  /** A dock input / select / textarea has the focus (the periodic re-render waits). */
  private editing(): boolean {
    const a = document.activeElement;
    return !!(a && this.root?.contains(a) && (a.tagName === 'INPUT' || a.tagName === 'SELECT' || a.tagName === 'TEXTAREA'));
  }

  /** space campaign A — THE LOADED-HULL LAW's client mirror: what is aboard a hull, by owner (own lots and the
   *  courier crates this client sees; the port's crates from its aboard hauls). */
  private aboardOf(shipId: bigint): ReturnType<typeof summarizeAboard> {
    const conn = atlas.conn;
    const lots: AboardLot[] = [];
    if (!conn?.identity) return summarizeAboard(lots);
    try {
      const db = conn.db as unknown as {
        cargoLot: { iter(): Iterable<{ owner: { isEqual(o: unknown): boolean }; locId: string; tag: string; qty: number }> };
        portHaul?: { iter(): Iterable<{ state: string; shipId: bigint; haulId: bigint; qty: number }> };
      };
      for (const l of db.cargoLot.iter()) if (l.locId === `ship:${shipId}`) lots.push({ qty: l.qty, tag: l.tag, mine: l.owner.isEqual(conn.identity) });
      if (db.portHaul) for (const h of db.portHaul.iter()) if (h.state === 'aboard' && h.shipId === shipId) lots.push({ qty: h.qty, tag: `h:${h.haulId}`, mine: false });
    } catch { /* pre-publish */ }
    return summarizeAboard(lots);
  }

  /** The pilot's Trade skill level (the broker fee's haggle). */
  private tradeLevel(): number {
    const conn = atlas.conn;
    if (!conn?.identity) return 0;
    try {
      const db = conn.db as unknown as { pilotSkill: { iter(): Iterable<{ identity: { isEqual(o: unknown): boolean }; skillKey: string; level: number }> } };
      for (const sk of db.pilotSkill.iter()) if (sk.skillKey === 'trade' && sk.identity.isEqual(conn.identity)) return sk.level;
    } catch { /* not subscribed yet */ }
    return 0;
  }

  /** A muted detail line (`text` is plain — never innerHTML). */
  private noteLine(text: string, css = ''): HTMLDivElement {
    const d = document.createElement('div');
    d.textContent = text;
    d.style.cssText = 'color:var(--ax-muted);font:10px var(--ax-mono);' + css;
    return d;
  }

  private tabCss(active: boolean): string {
    return `background:${active ? 'var(--ax-sel)' : 'var(--ax-bg3)'};border:1px solid var(--ax-line2);border-radius:var(--ax-radius);` +
      'color:var(--ax-text);padding:4px 14px;cursor:pointer;font:11px var(--ax-mono)';
  }

  /** The hull a courier crate rides in (its tagged lots are at ship:<id>), or null when none is aboard any hull. */
  private crateCarrier(contractId: bigint): { name: string; active: boolean } | null {
    const conn = atlas.conn;
    if (!conn?.identity) return null;
    try {
      const db = conn.db as unknown as { cargoLot: { iter(): Iterable<{ owner: { isEqual(o: unknown): boolean }; locKind: string; locId: string; tag: string }> }; playerShip: { shipId: { find(id: bigint): { name: string; active: boolean } | undefined } } };
      for (const l of db.cargoLot.iter()) {
        if (l.tag !== `c:${contractId}` || l.locKind !== 'ship' || !l.owner.isEqual(conn.identity)) continue;
        const ship = db.playerShip.shipId.find(BigInt(l.locId.replace(/^ship:/, '')));
        if (ship) return { name: ship.name, active: ship.active };
      }
    } catch { /* pre-publish */ }
    return null;
  }
  /** How many courier crates (distinct contracts) a hull carries. */
  private cratesAboard(shipId: bigint): number {
    const conn = atlas.conn;
    if (!conn?.identity) return 0;
    const seen = new Set<string>();
    try {
      const db = conn.db as unknown as { cargoLot: { iter(): Iterable<{ owner: { isEqual(o: unknown): boolean }; locKind: string; locId: string; tag: string }> } };
      for (const l of db.cargoLot.iter()) if (l.locKind === 'ship' && l.locId === `ship:${shipId}` && l.tag.startsWith('c:') && l.owner.isEqual(conn.identity)) seen.add(l.tag);
    } catch { /* pre-publish */ }
    return seen.size;
  }
  /** Guilds whose open motions the Wars tab subscribed (space campaign B). */
  private readonly wardecSubbed = new Set<string>();
  private button(label: string, enabled: boolean, onClick: () => void | Promise<unknown>): HTMLButtonElement {
    const b = document.createElement('button');
    b.textContent = label;
    b.disabled = !enabled;
    b.style.cssText = BTN + (enabled ? '' : ';opacity:.4;cursor:default');
    b.onclick = () => {
      if (!enabled) return;
      // Reducer refusals surface in the panel — never an uncaught rejection.
      const r = onClick();
      if (r && typeof (r as Promise<unknown>).then === 'function') {
        (r as Promise<unknown>).then(
          () => { sfx('sfx_coin_purchase', { gain: 0.3, cooldownMs: 300 }); this.status = ''; this.render(); },
          (e: Error) => { sfx('sfx_error', { gain: 0.25, cooldownMs: 300 }); this.status = `⚠ ${String(e?.message ?? e)}`; this.render(); },
        );
      }
      this.render();
    };
    return b;
  }

  /** `<b>name</b> <span muted>suffix</span>` built with textContent (XSS-safe). */
  private nameSpan(name: string, suffix: string): HTMLSpanElement {
    const wrap = document.createElement('span');
    const b = document.createElement('b');
    b.textContent = name;
    const s = document.createElement('span');
    s.textContent = ` ${suffix}`;
    s.style.color = 'var(--ax-muted)';
    wrap.append(b, s);
    return wrap;
  }

  private row(css = ''): HTMLDivElement {
    const d = document.createElement('div');
    d.style.cssText = 'display:flex;align-items:center;gap:10px;background:var(--ax-bg2);border:1px solid var(--ax-line);' +
      'border-radius:var(--ax-radius);padding:7px 10px;' + css;
    return d;
  }

  private render(): void {
    const conn = atlas.conn;
    const body = this.body;
    if (!body || !conn?.identity) return;
    const me = conn.identity;

    // Mirrors (guarded — the row cache can churn mid-iter).
    let sov = 0n;
    const ships: Array<{ shipId: bigint; chassisKey: string; name: string; active: boolean }> = [];
    const comps: Array<{ compId: bigint; shipId: bigint; slot: string; itemKey: string; name: string; quality: number }> = [];
    const items: Array<{ itemId: bigint; itemKey: string; name: string; quality: number }> = [];
    let faction = '', rank = 0, xp = 0n, tokens = 0n, arcStep = 0;
    const missions: Array<{ missionId: bigint; kind: string; targetPoi: number; goal: number; progress: number; rewardSov: bigint; state: string }> = [];
    try {
      const w = conn.db.wallet.identity.find(me);
      if (w) sov = w.sov;
      for (const s of conn.db.playerShip.iter()) if (s.owner.isEqual(me)) ships.push(s);
      for (const c of conn.db.shipComponent.iter()) comps.push(c);
      for (const it of conn.db.playerItem.iter()) {
        if (it.owner.isEqual(me) && slotOfItem(it.itemKey)) items.push(it);
      }
      const p = conn.db.pilotState.identity.find(me);
      if (p) { faction = p.factionKey; rank = p.rank; xp = p.xp; tokens = p.tokens; arcStep = p.arcStep; }
      for (const m of conn.db.pilotMission.iter()) {
        if (m.identity.isEqual(me) && m.state !== 'claimed') missions.push(m);
      }
    } catch { /* mid-churn — next tick */ }
    const activeShip = ships.find((s) => s.active) ?? ships[0] ?? null;
    const shipComps = activeShip ? comps.filter((c) => c.shipId === activeShip.shipId) : [];

    if (this.pilotEl) {
      const fac = faction === 'aurelian' ? 'Aurelian Concord' : faction === 'borean' ? 'Borean Compact' : 'Unaligned';
      this.pilotEl.textContent =
        `${fac} · rank ${rank} · ${xp} xp · ${tokens} tokens · ${sov} ⓒ` +
        (activeShip ? ` · flying: ${activeShip.name}` : ' · flying: stock Lancer');
    }
    for (const b of this.root?.querySelectorAll('button[data-tab]') ?? []) {
      (b as HTMLButtonElement).style.cssText = this.tabCss((b as HTMLElement).dataset.tab === this.tab);
    }

    body.replaceChildren();
    if (this.status) {
      const s = document.createElement('div');
      s.textContent = this.status;
      s.style.cssText = 'color:var(--ax-hi);font:11px var(--ax-mono)';
      body.appendChild(s);
    }

    if (this.tab === 'yard') {
      for (const spec of Object.values(CHASSIS)) {
        const owned = ships.some((s) => s.chassisKey === spec.key);
        const r = this.row();
        const info = document.createElement('div');
        info.style.cssText = 'flex:1';
        info.append(
          this.nameSpan(spec.name, `— ${spec.role}`),
          Object.assign(document.createElement('div'), {
            textContent: `spd ${spec.maxSpeed} · hull ${spec.hp} · shield ${spec.shield} · cargo ${spec.cargo}`,
          }),
        );
        (info.lastChild as HTMLElement).style.cssText = 'color:var(--ax-soft);font:10px var(--ax-mono)';
        const price = document.createElement('div');
        price.style.cssText = 'font:11px var(--ax-mono);color:var(--ax-text);white-space:nowrap';
        price.textContent = spec.costSov === 0 ? 'stock' : `${spec.costSov} ⓒ · rank ${spec.rank}`;
        r.append(info, price);
        if (spec.costSov > 0) {
          const can = !owned && sov >= BigInt(spec.costSov) && rank >= spec.rank;
          r.appendChild(this.button(owned ? 'Owned' : 'Buy', can, () => {
            this.status = `Buying ${spec.name}…`;
            return conn.reducers.buyShip({ chassisKey: spec.key });
          }));
        } else if (owned) {
          r.appendChild(this.button('Owned', false, () => { /* stock */ }));
        }
        body.appendChild(r);
      }
    } else if (this.tab === 'hangar') {
      if (!ships.length) {
        const d = document.createElement('div');
        d.textContent = 'Your hangar is empty — launch once and the stock Lancer is issued as a real hull (fit it in Loadout).';
        d.style.cssText = 'color:var(--ax-muted)';
        body.appendChild(d);
      }
      for (const s of ships) {
        const spec = CHASSIS[s.chassisKey] ?? CHASSIS[DEFAULT_CHASSIS];
        const r = this.row(s.active ? 'border-color:var(--ax-accent)' : '');
        r.style.alignItems = 'flex-start';
        const info = document.createElement('div');
        info.style.cssText = 'flex:1;display:flex;flex-direction:column;gap:2px';
        // s.name is PLAYER-SET (renameShip) — never innerHTML replicated strings.
        const crates = this.cratesAboard(s.shipId);
        info.appendChild(this.nameSpan(s.name, `— ${spec.name}${s.active ? ' · ACTIVE' : ''}${crates ? ` · carries ${crates} courier crate${crates > 1 ? 's' : ''}` : ''}`));
        // space campaign A — THE LOADED-HULL LAW: a hull with anything aboard cannot be broken up (the server
        // refuses with the same words); say what is aboard and how to clear it before the click.
        const aboard = this.aboardOf(s.shipId);
        const scrapWhy = scrapRefusal(aboard, `"${s.name}"`);
        if (scrapWhy) info.appendChild(this.noteLine(`⚠ ${scrapWhy}`, 'color:var(--ax-warn)'));
        // space campaign A — the loss preview (insureShip / destroyShipCore's terms): what a loss pays, what it
        // does not cover, and the stock starter's free replacement.
        const cost = BigInt(spec.costSov ?? 0);
        let standing = false;
        let payout: bigint | null = null;
        try {
          const pol = (conn.db as unknown as { shipInsurance: { shipId: { find(id: bigint): { expiresAt: { microsSinceUnixEpoch: bigint }; payoutSov: bigint } | null | undefined } } }).shipInsurance.shipId.find(s.shipId);
          standing = !!pol && Number(pol.expiresAt.microsSinceUnixEpoch / 1000n) > Date.now();
          if (standing && pol) {
            payout = pol.payoutSov;
            const daysLeft = Math.max(0, Math.round((Number(pol.expiresAt.microsSinceUnixEpoch / 1000n) - Date.now()) / 86_400_000));
            info.appendChild(this.noteLine(`🛡 insured — pays ${pol.payoutSov} ⓒ · ${daysLeft} d left`, 'color:var(--ax-good)'));
          }
        } catch { /* pre-publish server */ }
        const lp = lossPreview({
          hullName: spec.name, hullCostSov: cost, policyPayout: payout,
          fittedModules: comps.filter((c) => c.shipId === s.shipId).length,
          ownUnits: aboard.ownUnits, courierCrates: aboard.courierCrates, portUnits: aboard.portUnits,
        });
        const loss = document.createElement('div');
        loss.dataset.lossPreview = String(s.shipId);
        loss.style.cssText = 'display:flex;flex-direction:column;gap:1px;margin-top:2px';
        loss.append(this.noteLine(`If she is lost: ${lp.pays}`), this.noteLine(`Not covered: ${lp.notCovered.join(' · ')}.`));
        if (lp.also) loss.appendChild(this.noteLine(lp.also));
        if (lp.starter) loss.appendChild(this.noteLine(lp.starter));
        info.appendChild(loss);
        r.appendChild(info);
        const acts = document.createElement('div');
        acts.style.cssText = 'display:flex;flex-direction:column;gap:4px;align-items:stretch';
        if (!s.active) acts.appendChild(this.button('Make active', true, () => conn.reducers.setActiveShip({ shipId: s.shipId })));
        acts.appendChild(this.button('Rename', true, () => {
          const name = window.prompt('Ship name', s.name);
          if (name?.trim()) return conn.reducers.renameShip({ shipId: s.shipId, name: name.trim() });
        }));
        acts.appendChild(this.button(scrapWhy ? '⛔ Scrap — empty the hold' : '🔧 Scrap', !scrapWhy, () => {
          if (window.confirm(`Break up ${s.name}? Components return to your inventory; the insurance policy (if any) ends with her.`)) {
            return conn.reducers.scrapShip({ shipId: s.shipId });
          }
        }));
        if (!standing && cost > 0n) {
          const q = insuranceQuote(cost);
          acts.appendChild(this.button(`🛡 Insure (${q.premium} ⓒ)`, sov >= q.premium, () =>
            (conn.reducers as unknown as { insureShip(a: { shipId: bigint }): Promise<void> }).insureShip({ shipId: s.shipId })));
        }
        r.appendChild(acts);
        body.appendChild(r);
      }
    } else if (this.tab === 'loadout') {
      if (!activeShip) {
        const d = document.createElement('div');
        d.textContent = 'No hull row yet — launch once and the stock Lancer is issued with its ten hardpoints.';
        d.style.cssText = 'color:var(--ax-muted)';
        body.appendChild(d);
      } else {
        const st = shipStats(activeShip.chassisKey, shipComps);
        const sum = document.createElement('div');
        sum.style.cssText = 'font:10px var(--ax-mono);color:var(--ax-soft)';
        sum.textContent =
          `derived: spd ${Math.round(st.maxSpeed)} · hull ${st.hp} · shield ${st.shield} · cap ${st.capacitor}` +
          ` · dmg ×${st.weaponMult.toFixed(2)} · missiles ${st.missiles} · counters ${st.counters}`;
        body.appendChild(sum);
        // FITTING BUDGET: the chassis' CPU/powergrid vs the loadout draw —
        // better modules draw more; you cannot max every slot.
        const budget = FIT_BUDGET[activeShip.chassisKey] ?? { cpu: 200, pg: 200 };
        let cpuUsed = 0, pgUsed = 0;
        for (const c of shipComps) { const fc = fitCost(c.slot, c.quality); cpuUsed += fc.cpu; pgUsed += fc.pg; }
        const fitBar = (label: string, used: number, max: number): HTMLDivElement => {
          const wrap = document.createElement('div');
          wrap.style.cssText = 'display:flex;align-items:center;gap:8px;font:10px var(--ax-mono);color:var(--ax-soft)';
          const l = document.createElement('span');
          l.textContent = `${label} ${used}/${max}`;
          l.style.width = '110px';
          const track = document.createElement('div');
          track.style.cssText = 'flex:1;height:6px;background:var(--ax-bg2);border:1px solid var(--ax-line);border-radius:var(--ax-radius);overflow:hidden';
          const fill = document.createElement('div');
          const pct = Math.min(100, Math.round(used / max * 100));
          fill.style.cssText = `height:100%;width:${pct}%;background:${used > max ? 'var(--ax-bad)' : pct > 85 ? 'var(--ax-accent)' : 'var(--ax-accent)'}`;
          track.appendChild(fill);
          wrap.append(l, track);
          return wrap;
        };
        body.appendChild(fitBar('CPU', cpuUsed, budget.cpu));
        body.appendChild(fitBar('POWERGRID', pgUsed, budget.pg));
        for (const slot of SLOTS) {
          const fitted = shipComps.find((c) => c.slot === slot);
          const r = this.row();
          const label = document.createElement('div');
          label.style.cssText = 'width:120px;color:var(--ax-muted);font:11px var(--ax-mono)';
          label.textContent = SLOT_LABEL[slot as FleetSlot];
          const val = document.createElement('div');
          val.style.cssText = 'flex:1';
          val.textContent = fitted ? `${fitted.name} · q${fitted.quality}` : '—';
          r.append(label, val);
          if (fitted) {
            r.appendChild(this.button('Unequip', true, () =>
              conn.reducers.unequipShipComponent({ shipId: activeShip.shipId, slot })));
          }
          body.appendChild(r);
        }
        // space campaign I: the two fire groups over these mounts (what the primary and secondary triggers fire),
        // stored per hull and re-checked against this fit's revision.
        renderFireGroups(body, {
          shipId: String(activeShip.shipId), fit: { missiles: st.missiles, counters: st.counters }, fitRev: fitRevision(shipComps),
          onNote: (t) => { this.status = t; },
        });
        const invTitle = document.createElement('div');
        invTitle.textContent = 'COMPONENT INVENTORY';
        invTitle.style.cssText = 'font:700 10px var(--ax-mono);color:var(--ax-muted);letter-spacing:1.2px;margin-top:4px';
        body.appendChild(invTitle);
        if (!items.length) {
          const d = document.createElement('div');
          d.textContent = 'No ship components — pirates drop them, stations will sell them.';
          d.style.cssText = 'color:var(--ax-muted)';
          body.appendChild(d);
        }
        for (const it of items) {
          const slot = slotOfItem(it.itemKey) as string;
          let cpuFree = 0, pgFree = 0;
          for (const c of shipComps) {
            if (c.slot === slot) continue;
            const fc = fitCost(c.slot, c.quality);
            cpuFree += fc.cpu; pgFree += fc.pg;
          }
          const nc = fitCost(slot, it.quality);
          const fits = cpuFree + nc.cpu <= budget.cpu && pgFree + nc.pg <= budget.pg;
          const r = this.row();
          const info = document.createElement('div');
          info.style.cssText = 'flex:1';
          info.textContent = `${it.name} · q${it.quality} → ${SLOT_LABEL[slotOfItem(it.itemKey) as FleetSlot]} · draws ${nc.cpu} cpu / ${nc.pg} pg`;
          // space campaign J (report §17.4): a quality badge is never a free upgrade — both outcomes before the swap.
          const fittedQ = shipComps.find((c) => c.slot === slot)?.quality ?? null;
          const to = qualityTradeoff(activeShip.chassisKey, slot, it.itemKey, it.quality, fittedQ);
          if (to) {
            const tl = document.createElement('div');
            tl.dataset.tradeoff = slot;
            tl.style.cssText = `font:10px var(--ax-mono);color:${to.gainPct !== null && to.drawPct !== null && to.drawPct > to.gainPct ? 'var(--ax-warn)' : 'var(--ax-muted)'};margin-top:2px`;
            tl.textContent = to.line;
            info.appendChild(tl);
          }
          r.append(info, this.button(fits ? 'Equip' : 'Over budget', fits, () =>
            conn.reducers.equipShipComponent({ shipId: activeShip.shipId, itemId: it.itemId })));
          body.appendChild(r);
        }
      }
    } else if (this.tab === 'exchange') {
      // ── Regional exchange: prices drift per station; core sells basics
      // cheap, the rim pays premiums — hauling is the arbitrage.
      // space campaign A (gap 20): the rows are the server catalog's projection (EXCHANGE_GOODS) and every price is
      // exchangeUnitPrice — the SAME function spaceMarketBuy / spaceMarketSell charge through.
      const { systemId, stationKey } = this.stationCtx();
      const tier = sysTierOf(systemId);
      const epoch = marketEpoch(BigInt(Date.now()) * 1000n);
      const head = document.createElement('div');
      head.style.cssText = 'color:var(--ax-soft);font:11px var(--ax-mono)';
      head.textContent = `${stationKey} exchange · ${tier === 1 ? 'CORE prices (basics glutted)' : tier === 3 ? 'RIM prices (basics at a premium)' : 'mid-belt prices'} · marks drift every 2 h`;
      body.appendChild(head);
      body.appendChild(this.noteLine('No broker fee or tax here: the station is the counterparty and its price already carries a ±12 % spread. The Orders tab (player to player) charges a broker fee and a fill tax.'));
      // THE WAREHOUSE LAW (Space Wave 2): what sells HERE is the hold + your
      // warehouse at this station — never the ground bag, never a consigned crate.
      const stock: Record<string, number> = {};
      try {
        const activeId = [...conn.db.playerShip.iter()].find((s) => s.owner.isEqual(me) && s.active)?.shipId ?? null;
        for (const l of conn.db.cargoLot.iter()) {
          if (!l.owner.isEqual(me) || l.tag) continue;
          const here = (l.locKind === 'ship' && activeId !== null && l.locId === `ship:${activeId}`) || (l.locKind === 'station' && l.locId === stationKey);
          if (!here) continue;
          const c = commodityOfFamily(l.familyKey);
          if (c) stock[c] = (stock[c] ?? 0) + l.qty;
        }
      } catch { /* fine */ }
      for (const g of EXCHANGE_GOODS) {
        // Space Wave 3 (THE YARD LAW): a station's shortage multiplies both sides of the book.
        let mod = 1;
        try { for (const m of conn.db.spacePriceMod.iter()) if (m.stationKey === stationKey && m.commodity === g.commodity) { mod = m.mult; break; } } catch { /* not subscribed yet */ }
        // THE DEPTH LAW (the port wave): what this epoch already bought lifts the mark, what it sold sinks it — the server's own rule.
        let boughtU = 0, soldU = 0;
        try {
          const ddb = conn.db as unknown as { spaceMarketDepth: { iter(): Iterable<{ stationKey: string; commodity: string; epoch: bigint; boughtU: number; soldU: number }> } };
          for (const d of ddb.spaceMarketDepth.iter()) if (d.stationKey === stationKey && d.commodity === g.commodity && d.epoch === BigInt(epoch)) { boughtU = d.boughtU; soldU = d.soldU; break; }
        } catch { /* not subscribed yet */ }
        // space campaign K: a trade hub is deeper — the server prices with depthUnitsAt, and so does the dock.
        const buyPx = exchangeUnitPrice(g.baseValue, stationKey, g.commodity, epoch, tier, g.tier, mod, clientDepthUnits(stationKey, boughtU), 'buy');
        const sellPx = exchangeUnitPrice(g.baseValue, stationKey, g.commodity, epoch, tier, g.tier, mod, clientDepthUnits(stationKey, soldU), 'sell');
        const r = this.row();
        r.dataset.exchangeRow = g.commodity;
        const d = document.createElement('div');
        d.style.cssText = 'flex:1';
        d.textContent = `${g.label} — buy ${buyPx} ⓒ · sell ${sellPx} ⓒ · hold ${stock[g.commodity] ?? 0}${mod > 1 ? ` · ×${mod.toFixed(2)} — the yard is short` : ''}`;
        r.append(d,
          this.button('⬇ Buy 10', sov >= buyPx * 10n, () =>
            conn.reducers.spaceMarketBuy({ stationKey, commodity: g.commodity, qty: 10 })),
          this.button('⬆ Sell 10', (stock[g.commodity] ?? 0) >= 10, () =>
            conn.reducers.spaceMarketSell({ stationKey, commodity: g.commodity, qty: 10 })));
        body.appendChild(r);
      }
      const note = document.createElement('div');
      note.style.cssText = 'color:var(--ax-muted);font:10px var(--ax-font)';
      note.textContent = 'Mined ore and salvage sell here too (their families bridge to the exchange). A courier hold full of cheap core staples pays for itself rimward.';
      body.appendChild(note);
      body.appendChild(this.noteLine(`Not traded here: ${NOT_ON_EXCHANGE.map((n) => `${n.commodity} — ${n.why}`).join('; ')}. Crafted and ground goods trade at a town's market.`));
    } else if (this.tab === 'orders') {
      // ── The ORDER BOOK: standing bids and asks per resource family. FIFO
      // fills at the resting price; 3 % broker at placement, 5 % tax per fill.
      const { stationKey } = this.stationCtx();
      const db = conn.db as unknown as { marketOrder: { iter(): Iterable<{ orderId: bigint; owner: { isEqual(o: unknown): boolean }; stationKey: string; side: string; familyKey: string; priceSov: bigint; qty: number }> } };
      const orders = (() => { try { return [...db.marketOrder.iter()].filter((o) => o.stationKey === stationKey); } catch { return []; } })();
      const head = document.createElement('div');
      head.style.cssText = 'color:var(--ax-soft);font:11px var(--ax-mono)';
      const tradeLvl = this.tradeLevel();
      head.textContent = `${stationKey} order book · ${orders.length} standing orders · broker ${orderQuote('sell', 1n, 1, tradeLvl).brokerBps / 100} % at placement · fill tax 5 % per fill`;
      body.appendChild(head);
      // space campaign A: the families come from the Exchange catalog (all fifteen a hold carries), not a hand list.
      const FAMS = EXCHANGE_GOODS.flatMap((g) => g.families);
      const fm = this.orderForm;
      if (!FAMS.includes(fm.fam)) fm.fam = FAMS[0];
      // Placement form (its values live on the dock, so a re-render never resets what you typed).
      const form = this.row();
      const sel = document.createElement('select');
      sel.style.cssText = 'background:var(--ax-bg2);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);color:var(--ax-text);padding:2px 4px;font:11px var(--ax-mono)';
      for (const f of FAMS) { const o = document.createElement('option'); o.value = f; o.textContent = f.replace(/_/g, ' '); sel.appendChild(o); }
      sel.value = fm.fam;
      const sideSel = document.createElement('select');
      sideSel.style.cssText = sel.style.cssText;
      for (const sd of ['buy', 'sell']) { const o = document.createElement('option'); o.value = sd; o.textContent = sd.toUpperCase(); sideSel.appendChild(o); }
      sideSel.value = fm.side;
      const px = document.createElement('input'); px.type = 'number'; px.placeholder = 'price'; px.value = fm.px;
      const qy = document.createElement('input'); qy.type = 'number'; qy.placeholder = 'qty'; qy.value = fm.qty;
      for (const inp of [px, qy]) inp.style.cssText = 'width:64px;background:var(--ax-bg2);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);color:var(--ax-text);padding:2px 6px;font:11px var(--ax-mono)';
      form.append(sel, sideSel, px, qy, this.button('📝 Place', true, () =>
        (conn.reducers as unknown as { placeMarketOrder(a: { stationKey: string; side: string; familyKey: string; priceSov: bigint; qty: number }): Promise<void> })
          .placeMarketOrder({ stationKey, side: sideSel.value, familyKey: sel.value, priceSov: BigInt(Math.max(1, Number(px.value) | 0)), qty: Math.max(1, Number(qy.value) | 0) })));
      body.appendChild(form);
      // space campaign A — THE HONEST QUOTE (report §17.1): broker fee and fill tax, separately, before you place.
      const quote = document.createElement('div');
      quote.dataset.orderQuote = '1';
      quote.style.cssText = 'display:flex;flex-direction:column;gap:1px;padding:2px 4px';
      const drawQuote = (): void => {
        fm.fam = sel.value; fm.side = sideSel.value === 'buy' ? 'buy' : 'sell'; fm.px = px.value; fm.qty = qy.value;
        const q = orderQuote(fm.side, BigInt(Math.max(1, Number(px.value) | 0)), Math.max(1, Number(qy.value) | 0), tradeLvl);
        quote.replaceChildren(...q.lines.map((t, i) => this.noteLine(t, i === 2 && q.taxFree ? 'color:var(--ax-warn)' : '')));
      };
      for (const el of [sel, sideSel, px, qy]) { el.addEventListener('input', drawQuote); el.addEventListener('change', drawQuote); }
      drawQuote();
      body.appendChild(quote);
      // The book per family: best bid/ask + depth.
      for (const f of FAMS) {
        const asks = orders.filter((o) => o.familyKey === f && o.side === 'sell').sort((x, y) => (x.priceSov < y.priceSov ? -1 : 1));
        const bids = orders.filter((o) => o.familyKey === f && o.side === 'buy').sort((x, y) => (x.priceSov > y.priceSov ? -1 : 1));
        if (!asks.length && !bids.length) continue;
        const r = this.row();
        const d = document.createElement('div');
        d.style.cssText = 'flex:1;font:11px var(--ax-mono)';
        const ask0 = asks[0] ? `ask ${asks[0].priceSov} ⓒ ×${asks.reduce((n, o) => n + o.qty, 0)}` : 'no asks';
        const bid0 = bids[0] ? `bid ${bids[0].priceSov} ⓒ ×${bids.reduce((n, o) => n + o.qty, 0)}` : 'no bids';
        d.textContent = `${f.replace(/_/g, ' ')} — ${bid0} · ${ask0}`;
        r.appendChild(d);
        body.appendChild(r);
      }
      // My standing orders.
      const mine = orders.filter((o) => o.owner.isEqual(me));
      if (mine.length) {
        const mh = document.createElement('div');
        mh.style.cssText = 'color:var(--ax-muted);font:700 10px var(--ax-mono);letter-spacing:1px;margin-top:6px';
        mh.textContent = 'MY ORDERS';
        body.appendChild(mh);
        for (const o of mine) {
          const r = this.row();
          const d = document.createElement('div');
          d.style.cssText = 'flex:1;font:11px var(--ax-mono)';
          d.textContent = `${o.side.toUpperCase()} ${o.qty} × ${o.familyKey.replace(/_/g, ' ')} @ ${o.priceSov} ⓒ`;
          r.append(d, this.button('Cancel', true, () =>
            (conn.reducers as unknown as { cancelMarketOrder(a: { orderId: bigint }): Promise<void> }).cancelMarketOrder({ orderId: o.orderId })));
          body.appendChild(r);
        }
      }
    } else if (this.tab === 'haulage') {
      // ── COURIER CONTRACTS: collateral-backed hauling. The crate rides in
      // YOUR hold — lose the ship, lose the collateral.
      const { stationKey } = this.stationCtx();
      interface CourierRow { contractId: bigint; issuer: { isEqual(o: unknown): boolean }; courier: { isEqual(o: unknown): boolean }; originKey: string; destKey: string; cargoJson: string; rewardSov: bigint; collateralSov: bigint; state: string; expiresAt: { microsSinceUnixEpoch: bigint } }
      const db = conn.db as unknown as { courierContract: { iter(): Iterable<CourierRow> } };
      const rows = (() => { try { return [...db.courierContract.iter()]; } catch { return []; } })();
      const cargoLabel = (j: string): string => {
        try { return (JSON.parse(j) as Array<{ familyKey: string; qty: number }>).map((c) => `${c.qty}×${c.familyKey.replace(/_/g, ' ')}`).join(', '); }
        catch { return '?'; }
      };
      const head = document.createElement('div');
      head.style.cssText = 'color:var(--ax-soft);font:11px var(--ax-mono)';
      head.textContent = `Haulage board — you dock at ${stationKey}. Deliver AT the destination station.`;
      body.appendChild(head);
      // Post a contract from here.
      const form = this.row();
      // space campaign J: the form's values live on the dock (the Markets tab prefills them; the re-render keeps them).
      const cf = this.courierForm;
      const dest = document.createElement('input'); dest.placeholder = 'dest (sys9:st0)'; dest.value = cf.dest; dest.dataset.courierField = 'dest';
      const fam = document.createElement('input'); fam.placeholder = 'family'; fam.value = cf.fam; fam.dataset.courierField = 'family';
      const q2 = document.createElement('input'); q2.type = 'number'; q2.placeholder = 'qty'; q2.value = cf.qty; q2.dataset.courierField = 'qty';
      const rw = document.createElement('input'); rw.type = 'number'; rw.placeholder = 'reward'; rw.value = cf.reward; rw.dataset.courierField = 'reward';
      const co = document.createElement('input'); co.type = 'number'; co.placeholder = 'collateral'; co.value = cf.collateral; co.dataset.courierField = 'collateral';
      for (const inp of [dest, fam, q2, rw, co]) inp.style.cssText = 'width:82px;background:var(--ax-bg2);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);color:var(--ax-text);padding:2px 6px;font:11px var(--ax-mono)';
      const basisNote = this.noteLine('');
      basisNote.dataset.courierBasis = '1';
      const drawBasis = (): void => {
        cf.dest = dest.value; cf.fam = fam.value; cf.qty = q2.value; cf.reward = rw.value; cf.collateral = co.value;
        const ck = collateralCheck([{ familyKey: fam.value.trim(), qty: Math.max(1, Number(q2.value) | 0) }], BigInt(Math.max(0, Number(co.value) | 0)));
        basisNote.textContent = `Collateral: ${ck.text}${ck.unknown.length ? ` (no catalog basis for ${ck.unknown.join(', ')})` : ''}. One clock: the contract must be accepted AND delivered within 24 h of posting.`;
        basisNote.style.color = ck.over ? 'var(--ax-warn)' : 'var(--ax-muted)';
      };
      for (const inp of [dest, fam, q2, rw, co]) inp.addEventListener('input', drawBasis);
      drawBasis();
      form.append(dest, fam, q2, rw, co, this.button('Post', true, () =>
        (conn.reducers as unknown as { createCourierContract(a: { originKey: string; destKey: string; cargo: Array<{ familyKey: string; qty: number }>; rewardSov: bigint; collateralSov: bigint; durationS: number }): Promise<void> })
          .createCourierContract({ originKey: stationKey, destKey: dest.value.trim(), cargo: [{ familyKey: fam.value.trim(), qty: Math.max(1, Number(q2.value) | 0) }], rewardSov: BigInt(Math.max(1, Number(rw.value) | 0)), collateralSov: BigInt(Math.max(0, Number(co.value) | 0)), durationS: 24 * 3600 })));
      body.appendChild(form);
      body.appendChild(basisNote);
      for (const c of rows.filter((x) => x.state === 'offered' || x.state === 'accepted').slice(0, 20)) {
        const minsLeft = Math.max(0, Math.round((Number(c.expiresAt.microsSinceUnixEpoch / 1000n) - Date.now()) / 60_000));
        const r = this.row();
        const d = document.createElement('div');
        d.style.cssText = 'flex:1;font:11px var(--ax-mono)';
        d.textContent = `${c.originKey} → ${c.destKey} · ${cargoLabel(c.cargoJson)} · pays ${c.rewardSov} ⓒ · bond ${c.collateralSov} ⓒ · ${minsLeft} min · ${c.state}`;
        r.appendChild(d);
        const R = conn.reducers as unknown as {
          acceptCourierContract(a: { contractId: bigint }): Promise<void>;
          deliverCourierContract(a: { contractId: bigint }): Promise<void>;
          cancelCourierContract(a: { contractId: bigint }): Promise<void>;
        };
        // space campaign J (report §17.2): the terms come first — Accept lives in the terms window.
        if (c.state === 'offered' && !c.issuer.isEqual(me)) r.appendChild(this.button('📜 Terms…', true, () => { courierWindow().open(c.contractId); }));
        if (c.state === 'offered' && c.issuer.isEqual(me)) r.appendChild(this.button('Cancel', true, () => R.cancelCourierContract({ contractId: c.contractId })));
        if (c.state === 'accepted' && c.courier.isEqual(me)) r.appendChild(this.button('📜 Terms', true, () => { courierWindow().open(c.contractId); }));
        if (c.state === 'accepted' && c.courier.isEqual(me)) {
          // THE CUSTODY LAW: the crate is a tagged lot in ONE hull's hold — say which, and warn when it is not the active one.
          const carrier = this.crateCarrier(c.contractId);
          if (carrier) {
            const w = document.createElement('span');
            w.style.cssText = carrier.active ? 'color:var(--ax-hi);font:11px var(--ax-mono)' : 'color:var(--ax-warn);font:700 11px var(--ax-mono)';
            w.textContent = carrier.active ? ` · aboard ${carrier.name} (active)` : ` · ⚠ aboard ${carrier.name}, NOT your active hull — make it active before you fly`;
            d.appendChild(w);
          }
          const atDest = c.destKey === stationKey;
          r.appendChild(this.button(atDest ? 'Deliver' : `Deliver at ${c.destKey}`, atDest, () => R.deliverCourierContract({ contractId: c.contractId })));
        }
        body.appendChild(r);
      }
      // ── PORT RUNS (the planetary port, 2026-09-17): a Commons city's ore to its civ's yard and back.
      // The crates stay the PORT's while you fly them — take a run DOWN here (docked at the yard) on its TERMS,
      // land a run UP here; a run down lands at the port city's spaceport (the interact key on the ground).
      // space campaign E (2026-09-30): Terms… (owner / custodian / loss rule / split / relay before you take it), a run
      // in your hold can be offered to one pilot, a run you are authorised for can be taken over, the receipt chain
      // after delivery, and — when nothing is offered — the port's own reasons with its numbers.
      {
        ensurePortRows();
        interface PortHaulRow { haulId: bigint; portId: bigint; direction: string; familyKey: string; qty: number; rewardSov: bigint; state: string; hauler: { isEqual(o: unknown): boolean }; stationKey: string; deadlineAt: { microsSinceUnixEpoch: bigint } }
        interface PortRow { portId: bigint; settlementId: bigint; state: string }
        const hdb = conn.db as unknown as { portHaul?: { iter(): Iterable<PortHaulRow> }; planetaryPort?: { iter(): Iterable<PortRow> } };
        const runs = (() => { try { return hdb.portHaul ? [...hdb.portHaul.iter()].filter((h) => h.state === 'offered' || h.state === 'aboard') : []; } catch { return []; } })();
        const ports = (() => { try { return hdb.planetaryPort ? [...hdb.planetaryPort.iter()] : []; } catch { return []; } })();
        const ph = document.createElement('div');
        ph.style.cssText = 'color:var(--ax-muted);font:700 10px var(--ax-mono);letter-spacing:1px;margin-top:8px;display:flex;gap:8px;align-items:center;flex-wrap:wrap';
        ph.textContent = `PORT RUNS — a town's ore to its yard and back · ${runs.length ? `${runs.length} live` : 'none on offer'}`;
        ph.appendChild(this.button('🧾 My receipts…', true, () => portWindow().open({ kind: 'receipts' })));
        body.appendChild(ph);
        const RP = conn.reducers as unknown as { portDeliver(a: { haulId: bigint }): Promise<void> };
        const K = keyHint('interact');
        for (const h of runs.slice(0, 8)) {
          const port = ports.find((p) => p.portId === h.portId);
          const minsLeft = Math.max(0, Math.round((Number(h.deadlineAt.microsSinceUnixEpoch / 1000n) - Date.now()) / 60_000));
          const mine = h.state === 'aboard' && h.hauler.isEqual(me);
          const t = portTermsOf(h.haulId);
          const role = relayRole(h as never);
          const r = this.row();
          const d = document.createElement('div');
          d.style.cssText = 'flex:1;font:11px var(--ax-mono)';
          const city = port ? portCityName(port.settlementId) : '?';
          const crew = t ? portCrewOf(t.splitJson).length : 0;
          d.textContent = `${h.direction === 'up' ? 'UP' : 'DOWN'} · ${h.qty} u ${h.familyKey.replace(/_/g, ' ')} · ${h.direction === 'up' ? `${city} → ${h.stationKey}` : `${h.stationKey} → ${city}`} · ${h.rewardSov} ⓒ escrowed · ${minsLeft} min · ${h.state}${mine ? ' (yours)' : ''}`
            + (t && h.state === 'aboard' ? ` · ${crew ? `crew ${crew}` : 'solo'}${t.custodianRev > 0 ? ' · relayed' : ''}` : '');
          r.appendChild(d);
          const here = h.stationKey === stationKey;
          if (h.state === 'offered' && h.direction === 'down') r.appendChild(this.button(here ? '📜 Terms…' : `Terms at ${h.stationKey}`, here, () => portWindow().open({ kind: 'terms', haulId: h.haulId })));
          else if (h.state === 'offered' && h.direction === 'up') r.appendChild(this.button(`Take it at ${city}'s spaceport (${K})`, false, () => undefined));
          else if (mine && h.direction === 'up') r.appendChild(this.button(here ? '⬇ Land the run' : `Land at ${h.stationKey}`, here, () => RP.portDeliver({ haulId: h.haulId }).then(() => portWindow().open({ kind: 'receipt', haulId: h.haulId }))));
          else if (mine && h.direction === 'down') r.appendChild(this.button(`Land it at ${city}'s spaceport (${K})`, false, () => undefined));
          if (role === 'captain') r.appendChild(this.button('⇄ Relay…', true, () => portWindow().open({ kind: 'run', haulId: h.haulId })));
          else if (role === 'receiver') r.appendChild(this.button('⇄ Take over…', true, () => portWindow().open({ kind: 'run', haulId: h.haulId })));
          body.appendChild(r);
        }
        // THE DRY CHAIN: a port with nothing in flight says why, in the numbers of its last hourly step.
        for (const p of ports) {
          if (runs.some((h) => h.portId === p.portId)) continue;
          const st = portStatusOf(p.portId);
          let reasons: string[] = [], numbers: Record<string, string | number> = {};
          try { if (st) { reasons = JSON.parse(st.reasonsJson) as string[]; numbers = JSON.parse(st.numbersJson) as Record<string, string | number>; } } catch { /* */ }
          // `pending` is the step's own view of an hour ago — whether a run is in flight NOW is the port_haul rows' (none here).
          const why = st ? dryLines(st.procure, reasons.filter((r) => r !== 'pending'), numbers) : ['the port has not reported since the last publish — it reports at its next hourly step'];
          const at = st ? new Date(Number(st.checkedAt.microsSinceUnixEpoch / 1000n)) : null;
          body.appendChild(this.noteLine(`${portCityName(p.settlementId)}'s port (${p.state})${at ? ` as of ${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}` : ''}: ${why.join('; ')}`, 'line-height:1.4'));
        }
      }
      // ── FREELANCE BOARDS: player-authored objectives, escrow-paid ──
      {
        const fh = document.createElement('div');
        fh.style.cssText = 'color:var(--ax-muted);font:700 10px var(--ax-mono);letter-spacing:1px;margin-top:8px';
        fh.textContent = 'FREELANCE BOARDS — den kills (shot down blow by blow; one kill pays one project) or delivered goods, paid per unit';
        body.appendChild(fh);
        interface ProjRow { projectId: bigint; issuer: { isEqual(o: unknown): boolean }; title: string; kind: string; targetKey: string; goal: number; progress: number; rewardPerSov: bigint; state: string }
        const pdb = conn.db as unknown as { freelanceProject: { iter(): Iterable<ProjRow> } };
        const projs = (() => { try { return [...pdb.freelanceProject.iter()].filter((p2) => p2.state === 'open'); } catch { return []; } })();
        const R2 = conn.reducers as unknown as {
          createFreelanceProject(a2: { title: string; kind: string; targetKey: string; goal: number; rewardPerSov: bigint; durationS: number }): Promise<void>;
          claimFreelanceKills(a2: { projectId: bigint }): Promise<void>;
          deliverFreelanceGoods(a2: { projectId: bigint; qty: number }): Promise<void>;
          cancelFreelanceProject(a2: { projectId: bigint }): Promise<void>;
        };
        const pform = this.row();
        const pt = document.createElement('input'); pt.placeholder = 'title'; pt.value = '';
        const pk = document.createElement('select');
        for (const kk of ['kills', 'deliver']) { const o = document.createElement('option'); o.value = kk; o.textContent = kk; pk.appendChild(o); }
        const ptk = document.createElement('input'); ptk.placeholder = 'family (deliver)'; ptk.value = 'grain';
        const pg = document.createElement('input'); pg.type = 'number'; pg.placeholder = 'goal'; pg.value = '10';
        const pp = document.createElement('input'); pp.type = 'number'; pp.placeholder = 'ⓒ/unit'; pp.value = '5';
        for (const inp of [pt, ptk, pg, pp]) inp.style.cssText = 'width:80px;background:var(--ax-bg2);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);color:var(--ax-text);padding:2px 6px;font:11px var(--ax-mono)';
        pk.style.cssText = 'background:var(--ax-bg2);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);color:var(--ax-text);padding:2px 4px;font:11px var(--ax-mono)';
        pform.append(pt, pk, ptk, pg, pp, this.button('Post board', true, () =>
          R2.createFreelanceProject({ title: pt.value.trim() || 'Untitled work', kind: pk.value, targetKey: ptk.value.trim(), goal: Math.max(1, Number(pg.value) | 0), rewardPerSov: BigInt(Math.max(1, Number(pp.value) | 0)), durationS: 48 * 3600 })));
        body.appendChild(pform);
        for (const p2 of projs.slice(0, 12)) {
          const r = this.row();
          const d = document.createElement('div');
          d.style.cssText = 'flex:1;font:11px var(--ax-mono)';
          d.textContent = `${p2.title} — ${p2.kind}${p2.kind === 'deliver' ? ` (${p2.targetKey.replace(/_/g, ' ')})` : p2.kind === 'escort' ? ' (fly with the run — E in flight)' : ''} · ${p2.progress}/${p2.goal} · ${p2.rewardPerSov} ⓒ${p2.kind === 'escort' ? ' at arrival' : '/unit'}`;
          r.appendChild(d);
          if (p2.issuer.isEqual(me)) r.appendChild(this.button('Cancel', true, () => R2.cancelFreelanceProject({ projectId: p2.projectId })));
          else if (p2.kind === 'kills') r.appendChild(this.button('Claim my kills', true, () => R2.claimFreelanceKills({ projectId: p2.projectId })));
          else if (p2.kind === 'deliver') r.appendChild(this.button('Deliver 10', true, () => R2.deliverFreelanceGoods({ projectId: p2.projectId, qty: 10 })));
          body.appendChild(r);
        }
      }
    } else if (this.tab === 'kills') {
      // ── KILL RECORDS: every hull loss is public history.
      interface KillRow { killId: bigint; victimName: string; chassisKey: string; shipName: string; systemKey: string; attackerKind: string; attackerRef: string; valueSov: bigint; at: { microsSinceUnixEpoch: bigint } }
      const db = conn.db as unknown as { killRecord: { iter(): Iterable<KillRow> } };
      const kills = (() => { try { return [...db.killRecord.iter()]; } catch { return []; } })()
        .sort((x, y) => (x.killId > y.killId ? -1 : 1)).slice(0, 30);
      const head = document.createElement('div');
      head.style.cssText = 'color:var(--ax-soft);font:11px var(--ax-mono)';
      head.textContent = `The kill board — ${kills.length} recent hull losses. Losses are REAL: the fit goes down with her.`;
      body.appendChild(head);
      if (!kills.length) {
        const d = document.createElement('div');
        d.style.cssText = 'color:var(--ax-muted)';
        d.textContent = 'No hulls lost yet. Fly dangerous.';
        body.appendChild(d);
      }
      for (const k of kills) {
        const mins = Math.round((Date.now() - Number(k.at.microsSinceUnixEpoch / 1000n)) / 60_000);
        const r = this.row();
        const d = document.createElement('div');
        d.style.cssText = 'flex:1;font:11px var(--ax-mono)';
        d.appendChild(this.nameSpan(`${k.victimName}`, `lost ${CHASSIS[k.chassisKey]?.name ?? k.chassisKey} in ${k.systemKey} · ${k.valueSov} ⓒ · ${k.attackerKind}${k.attackerRef ? ` (${k.attackerRef})` : ''} · ${mins < 60 ? `${mins} min` : `${Math.round(mins / 60)} h`} ago`));
        r.appendChild(d);
        body.appendChild(r);
      }
      // ── THE BOUNTY OFFICE: post a sealed writ on another pilot ──────
      const bh = document.createElement('div');
      bh.style.cssText = 'margin-top:10px;color:var(--ax-bad);font:11px var(--ax-mono)';
      bh.textContent = 'THE QUIET DESK — post a sealed writ. 400 ⓒ buys a Hired Blade; 1500 a Tracker; 5000 a Revenant; 15000 a Wraith. The guild never says who paid.';
      body.appendChild(bh);
      const form = this.row();
      const nameIn = document.createElement('input');
      nameIn.placeholder = 'pilot name';
      nameIn.style.cssText = 'flex:1;background:var(--ax-bg);border:1px solid var(--ax-line);color:var(--ax-text);font:11px var(--ax-mono);padding:4px 6px;border-radius:var(--ax-radius)';
      const spendIn = document.createElement('input');
      spendIn.placeholder = 'ⓒ';
      spendIn.value = '400';
      spendIn.style.cssText = 'width:70px;background:var(--ax-bg);border:1px solid var(--ax-line);color:var(--ax-text);font:11px var(--ax-mono);padding:4px 6px;border-radius:var(--ax-radius)';
      for (const el of [nameIn, spendIn]) {
        el.addEventListener('keydown', (ev) => ev.stopPropagation());
        el.addEventListener('keyup', (ev) => ev.stopPropagation());
        el.addEventListener('keypress', (ev) => ev.stopPropagation());
      }
      form.append(nameIn, spendIn, this.button('Post the writ', true, () => {
        const spend = BigInt(Math.max(0, Math.floor(Number(spendIn.value) || 0)));
        (conn.reducers as unknown as { placeBounty(a: { targetUsername: string; spend: bigint }): Promise<void> })
          .placeBounty({ targetUsername: nameIn.value.trim(), spend })
          .then(() => { nameIn.value = ''; this.render(); })
          .catch((e: Error) => { const d = document.createElement('div'); d.style.cssText = 'color:var(--ax-bad);font:10px var(--ax-mono)'; d.textContent = String(e.message ?? e); form.appendChild(d); });
      }));
      body.appendChild(form);
      // My writs (the guild reports outcomes to the buyer alone).
      interface WritRow { bountyId: bigint; placedBy: { isEqual(o: unknown): boolean }; state: string; hunterName: string; tier: number; spend: bigint }
      const writs = (() => {
        try {
          return [...(conn.db as unknown as { bountyContract: { iter(): Iterable<WritRow> } }).bountyContract.iter()]
            .filter((w) => w.placedBy.isEqual(me));
        } catch { return []; }
      })();
      for (const w of writs.sort((x, y) => (x.bountyId > y.bountyId ? -1 : 1)).slice(0, 8)) {
        const r = this.row();
        const d = document.createElement('div');
        d.style.cssText = 'flex:1;font:10px var(--ax-mono);color:var(--ax-soft)';
        const st = w.state === 'stalking' ? 'the hunter watches' : w.state === 'attacking' ? 'the hunter has committed'
          : w.state === 'fulfilled' ? 'CLOSED — the debt was collected'
            : w.state === 'lapsed' ? 'CLOSED — the hunter gave up (a hire lapses after 72 h)' : 'CLOSED — the hunter died';
        d.textContent = `writ #${w.bountyId} · ${w.hunterName} (T${w.tier}, ${w.spend} ⓒ) — ${st}`;
        r.appendChild(d);
        body.appendChild(r);
      }
    } else if (this.tab === 'wars') {
      // ── GUILD WARS: wardecs legalize sieges on the enemy's platforms ──
      interface GW { gwId: bigint; guildA: bigint; guildB: bigint; state: string; endsAt: { microsSinceUnixEpoch: bigint } }
      interface GRow { guildId: bigint; name: string; tag: string }
      interface GM { guildId: bigint; identity: { isEqual(o: unknown): boolean }; title: string }
      const gdb = conn.db as unknown as { guildWar: { iter(): Iterable<GW> }; guild: { iter(): Iterable<GRow> }; guildMember: { iter(): Iterable<GM> } };
      const R3 = conn.reducers as unknown as {
        declareGuildWar(a2: { targetGuildId: bigint }): Promise<void>;
        surrenderGuildWar(a2: { gwId: bigint }): Promise<void>;
        attackSpaceStructure(a2: { structureId: bigint }): Promise<void>;
        repairSpaceStructure(a2: { structureId: bigint }): Promise<void>;
      };
      const myM = (() => { try { return [...gdb.guildMember.iter()].find((m2) => m2.identity.isEqual(me)); } catch { return undefined; } })();
      const gname = (id: bigint): string => { try { const g = [...gdb.guild.iter()].find((x) => x.guildId === id); return g ? `${g.name} <${g.tag}>` : `guild ${id}`; } catch { return `guild ${id}`; } };
      // THE COURT DECIDES WAR (space campaign B, lib/wardec_rules): the Guild Master or a councillor lays a war / peace
      // motion before the guild court (it carries their yes; a court of one acts at once); the 500 ⓒ comes from the
      // guild TREASURY when it passes. A plain member is told who decides. The flag off = the legacy rule.
      const flagRow = (() => { try { return (conn.db as unknown as { platformFlag: { key: { find(k: string): { enabled: boolean } | undefined } } }).platformFlag.key.find(WARDEC_FLAG); } catch { return undefined; } })();
      const flagOn = flagRow ? flagRow.enabled : true;
      const courtSize = myM ? (() => { try { return [...gdb.guildMember.iter()].filter((m2) => m2.guildId === myM.guildId && WARDEC_COURT_TITLES.includes(m2.title)).length; } catch { return 1; } })() : 0;
      const declareRoute = wardecRoute({ flagOn, title: myM ? myM.title : null, courtSize, act: 'declare' });
      const surrenderRoute = wardecRoute({ flagOn, title: myM ? myM.title : null, courtSize, act: 'surrender' });
      const treasury = myM ? (() => { try { return conn.db.wallet.identity.find(new Identity(GUILD_VAULT_BASE + myM.guildId))?.sov ?? 0n; } catch { return 0n; } })() : 0n;
      if (myM && !this.wardecSubbed.has(String(myM.guildId))) {
        this.wardecSubbed.add(String(myM.guildId)); // the court's open motions (the guild window subscribes them only while open)
        try { conn.subscriptionBuilder().subscribe([`SELECT * FROM guild_motion WHERE guild_id = ${myM.guildId}`]); } catch { /* pre-publish */ }
      }
      const head = document.createElement('div');
      head.style.cssText = 'color:var(--ax-soft);font:11px var(--ax-mono)';
      head.textContent = !myM ? 'You fly no colors — join a guild (V) to make war.'
        : declareRoute.route === 'legacy' ? `Wars of ${gname(myM.guildId)} — a declaration costs 500 ⓒ from your wallet and runs 7 days`
          : `Wars of ${gname(myM.guildId)} — a war runs 7 days; while it stands, sieges on each other's platforms and space PvP between the two guilds are lawful. `
            + `A declaration costs 500 ⓒ from the guild treasury (it holds ${treasury} ⓒ). `
            + (declareRoute.route === 'refuse' ? `${declareRoute.reason}.` : declareRoute.passesAtOnce ? 'Your court is you alone: a war or a surrender acts at once.' : `Your court has ${courtSize} seats: your motion carries your yes, the majority decides (vote in the Guild window → Court).`);
      body.appendChild(head);
      const wars = (() => { try { return [...gdb.guildWar.iter()].filter((w) => w.state === 'declared'); } catch { return []; } })();
      // Open war / peace motions of my guild (the court's queue).
      if (myM) {
        try {
          for (const mo of (conn.db as unknown as { guildMotion: { iter(): Iterable<{ motionId: bigint; guildId: bigint; kind: string; text: string; yes: number; no: number; status: string; outcome: string }> } }).guildMotion.iter()) {
            if (mo.guildId !== myM.guildId || (mo.kind !== 'war' && mo.kind !== 'peace')) continue;
            if (mo.status !== 'open' && !mo.outcome) continue;
            const r = this.row();
            const d = document.createElement('div');
            d.style.cssText = 'flex:1;font:10px var(--ax-mono);color:var(--ax-soft)';
            d.dataset.wardecMotion = String(mo.motionId);
            d.textContent = `⚖ motion #${mo.motionId} · ${mo.text} · ${mo.yes} yes / ${mo.no} no · ${mo.status}${mo.outcome ? ` — ${mo.outcome}` : ''}`;
            r.appendChild(d);
            body.appendChild(r);
          }
        } catch { /* not subscribed yet */ }
      }
      for (const w of wars.slice(0, 10)) {
        const minesIt = myM && (w.guildA === myM.guildId || w.guildB === myM.guildId);
        const r = this.row(minesIt ? 'border-color:var(--ax-bad)' : '');
        const d = document.createElement('div');
        d.style.cssText = 'flex:1;font:11px var(--ax-mono)';
        const daysLeft = Math.max(0, Math.round((Number(w.endsAt.microsSinceUnixEpoch / 1000n) - Date.now()) / 86_400_000));
        d.textContent = `⚔ ${gname(w.guildA)} vs ${gname(w.guildB)} · ${daysLeft} d left`;
        r.appendChild(d);
        if (minesIt && surrenderRoute.route !== 'refuse') {
          const label = surrenderRoute.route === 'motion' && !surrenderRoute.passesAtOnce ? 'Lay a surrender before the court' : 'Surrender';
          r.appendChild(this.button(label, true, () => R3.surrenderGuildWar({ gwId: w.gwId })));
        }
        body.appendChild(r);
      }
      if (myM && declareRoute.route !== 'refuse') {
        const viaTreasury = declareRoute.route === 'motion';
        const canPay = viaTreasury ? treasury >= 500n : sov >= 500n;
        const label = !viaTreasury ? 'Declare war (500 ⓒ)' : declareRoute.passesAtOnce ? 'Declare war (500 ⓒ from the treasury)' : 'Lay a war motion (500 ⓒ from the treasury if it passes)';
        const others = (() => { try { return [...gdb.guild.iter()].filter((g) => g.guildId !== myM.guildId); } catch { return []; } })();
        for (const g of others.slice(0, 8)) {
          const atWar = wars.some((w) => (w.guildA === myM.guildId && w.guildB === g.guildId) || (w.guildB === myM.guildId && w.guildA === g.guildId));
          if (atWar) continue;
          const r = this.row();
          const d = document.createElement('div');
          d.style.cssText = 'flex:1;font:11px var(--ax-mono)';
          d.textContent = `${g.name} <${g.tag}>`;
          r.append(d, this.button(label, canPay, () => R3.declareGuildWar({ targetGuildId: g.guildId })));
          body.appendChild(r);
        }
      }
      // Siegeable enemy platforms + my platforms under siege.
      interface SS { structId: bigint; owner: { isEqual(o: unknown): boolean }; kind: string; systemId: string }
      interface Siege { structureId: bigint; hp: number; state: string; reinforcedUntil: { microsSinceUnixEpoch: bigint } }
      const sdb = conn.db as unknown as { spaceStructure: { iter(): Iterable<SS> }; structureSiege: { iter(): Iterable<Siege> } };
      const sieges = (() => { try { return [...sdb.structureSiege.iter()]; } catch { return []; } })();
      const sh = document.createElement('div');
      sh.style.cssText = 'color:var(--ax-muted);font:700 10px var(--ax-mono);letter-spacing:1px;margin-top:8px';
      sh.textContent = 'SIEGES — hp → 8 h reinforcement → the final battle';
      body.appendChild(sh);
      try {
        for (const st of [...sdb.spaceStructure.iter()].slice(0, 14)) {
          const siege = sieges.find((x) => x.structureId === st.structId);
          const minePlat = st.owner.isEqual(me);
          const rfLeft = siege && siege.state === 'reinforced' ? Math.max(0, Math.round((Number(siege.reinforcedUntil.microsSinceUnixEpoch / 1000n) - Date.now()) / 60_000)) : 0;
          const r = this.row(siege ? 'border-color:var(--ax-bad)' : '');
          const d = document.createElement('div');
          d.style.cssText = 'flex:1;font:11px var(--ax-mono)';
          d.textContent = `${st.kind} · ${st.systemId} · ${minePlat ? 'YOURS' : 'theirs'}${siege ? ` · ${siege.state === 'reinforced' ? (rfLeft > 0 ? `REINFORCED ${rfLeft} min` : 'timer OUT — final battle') : `${siege.hp} hp`}` : ''}`;
          r.appendChild(d);
          if (!minePlat) r.appendChild(this.button('Attack', true, () => R3.attackSpaceStructure({ structureId: st.structId })));
          else if (siege) r.appendChild(this.button('Repair', true, () => R3.repairSpaceStructure({ structureId: st.structId })));
          body.appendChild(r);
        }
      } catch { /* pre-publish */ }
    } else if (this.tab === 'skills') {
      // ── THE SKILL QUEUE: one slot, trains offline, changes real numbers ──
      const SKILLS: Record<string, string> = {
        gunnery: '+4 % bolt damage / level', shields: '+5 % shield pool / level',
        navigation: '+3 % speed / level', trade: 'broker fee −0.3 % / level (server-real)',
        salvaging: 'richer wreck strips / level', command: '+2 % squadron duty pay / level',
      };
      interface SkillRow { identity: { isEqual(o: unknown): boolean }; skillKey: string; level: number }
      interface TrainRow { identity: { isEqual(o: unknown): boolean }; skillKey: string; endsAt: { microsSinceUnixEpoch: bigint } }
      const kdb = conn.db as unknown as { pilotSkill: { iter(): Iterable<SkillRow> }; pilotTraining: { iter(): Iterable<TrainRow> } };
      const R4 = conn.reducers as unknown as { trainSkill(a2: { skillKey: string }): Promise<void> };
      const mySkills = (() => { try { return [...kdb.pilotSkill.iter()].filter((sk) => sk.identity.isEqual(me)); } catch { return []; } })();
      const training = (() => { try { return [...kdb.pilotTraining.iter()].find((tr) => tr.identity.isEqual(me)); } catch { return undefined; } })();
      const head = document.createElement('div');
      head.style.cssText = 'color:var(--ax-soft);font:11px var(--ax-mono)';
      head.textContent = training
        ? `Training ${training.skillKey} — done in ${Math.max(0, Math.round((Number(training.endsAt.microsSinceUnixEpoch / 1000n) - Date.now()) / 60_000))} min (offline counts)`
        : 'The training slot is idle — queue a skill. Level N takes N × 30 min, offline time included.';
      body.appendChild(head);
      for (const [key, desc] of Object.entries(SKILLS)) {
        const lvl = mySkills.find((sk) => sk.skillKey === key)?.level ?? 0;
        const r = this.row();
        const d = document.createElement('div');
        d.style.cssText = 'flex:1;font:11px var(--ax-mono)';
        d.textContent = `${key} ${'▰'.repeat(lvl)}${'▱'.repeat(5 - lvl)} — ${desc}`;
        r.appendChild(d);
        if (lvl < 5) r.appendChild(this.button(training ? 'Slot busy' : `Train L${lvl + 1} (${(lvl + 1) * 30} min)`, !training, () => R4.trainSkill({ skillKey: key })));
        body.appendChild(r);
      }
    } else if (this.tab === 'abyss') {
      // ── ABYSSAL POCKETS: filament in, 20 minutes or the ship is LOST ──
      interface RunRow { runId: bigint; identity: { isEqual(o: unknown): boolean }; tier: number; state: string; roomsClaimed: number; expiresAt: { microsSinceUnixEpoch: bigint } }
      const adb = conn.db as unknown as { abyssalRun: { iter(): Iterable<RunRow> } };
      const R5 = conn.reducers as unknown as {
        buyAbyssalFilament(a2: { tier: number }): Promise<void>;
        enterAbyssal(a2: { tier: number }): Promise<void>;
        claimAbyssalRoom(a2: { runId: bigint }): Promise<void>;
        exitAbyssal(a2: { runId: bigint }): Promise<void>;
      };
      const open = (() => { try { return [...adb.abyssalRun.iter()].find((r2) => r2.identity.isEqual(me) && r2.state === 'open'); } catch { return undefined; } })();
      const head = document.createElement('div');
      head.style.cssText = 'color:var(--ax-rare);font:11px var(--ax-mono)';
      head.textContent = open
        ? `INSIDE THE POCKET (t${open.tier}) — ${open.roomsClaimed}/3 chambers · ${Math.max(0, Math.round((Number(open.expiresAt.microsSinceUnixEpoch / 1000n) - Date.now()) / 60_000))} min before it TAKES THE SHIP`
        : 'The abyss: three chambers of pay in twenty minutes — overstay and the ship is LOST (killmail and all).';
      body.appendChild(head);
      const filaments = items.filter((it) => /^filament_t\d$/.test(it.itemKey));
      for (const tier of [1, 2, 3]) {
        const held = filaments.filter((f) => f.itemKey === `filament_t${tier}`).length;
        const cost = tier === 1 ? 80 : tier === 2 ? 200 : 450;
        const r = this.row();
        const d = document.createElement('div');
        d.style.cssText = 'flex:1;font:11px var(--ax-mono)';
        d.textContent = `t${tier} filament — ${cost} ⓒ · held ${held} · rooms pay ${tier * 60}/${tier * 80}/${tier * 100} ⓒ + t${tier} hardware`;
        r.append(d,
          this.button('Buy', sov >= BigInt(cost), () => R5.buyAbyssalFilament({ tier })),
          this.button('Enter', held > 0 && !open, () => R5.enterAbyssal({ tier })));
        body.appendChild(r);
      }
      if (open) {
        const r = this.row('border-color:var(--ax-line2)');
        r.append(
          this.button('Breach the next chamber', open.roomsClaimed < 3, () => R5.claimAbyssalRoom({ runId: open.runId })),
          this.button('Take the exit', true, () => R5.exitAbyssal({ runId: open.runId })));
        body.appendChild(r);
      }
    } else if (this.tab === 'industry') {
      // ── The refinery line: raw stock in, refined stock out (35% here;
      // your own refinery beacon runs at 50%).
      const head = document.createElement('div');
      head.style.cssText = 'color:var(--ax-soft);font:11px var(--ax-mono)';
      const lineEff = clientRefineEff(this.stationCtx().stationKey); // space campaign K: a refinery's line yields 45 %
      head.textContent = `Station refinery — ${Math.round(lineEff * 100)}% line yield · your own refinery beacon runs 50% (K deploys one) · refines what is HERE: aboard or in your warehouse`;
      body.appendChild(head);
      const { stationKey: hereKey } = this.stationCtx();
      const activeId2 = [...conn.db.playerShip.iter()].find((s) => s.owner.isEqual(me) && s.active)?.shipId ?? null;
      const lots: Array<{ lotId: bigint; name: string; familyKey: string; qty: number; locKind: string; tag: string }> = [];
      try {
        for (const l of conn.db.cargoLot.iter()) {
          if (!l.owner.isEqual(me)) continue;
          if ((l.locKind === 'ship' && activeId2 !== null && l.locId === `ship:${activeId2}`) || (l.locKind === 'station' && l.locId === hereKey)) lots.push(l);
        }
      } catch { /* fine */ }
      if (!lots.length) {
        const d = document.createElement('div');
        d.textContent = 'Nothing here — mine an asteroid field (hold U inside one), strip the war graves, or buy at the Exchange.';
        d.style.cssText = 'color:var(--ax-muted)';
        body.appendChild(d);
      }
      for (const l of lots) {
        const refined = l.name.startsWith('Refined ');
        const r = this.row();
        const d = document.createElement('div');
        d.style.cssText = 'flex:1';
        d.textContent = `${l.name} · ${l.familyKey.replace(/_/g, ' ')} · ${l.qty} u · ${l.locKind === 'ship' ? 'aboard' : 'warehouse'}${refined ? ' · REFINED' : ''}${l.tag ? ' · consigned' : ''}`;
        r.appendChild(d);
        if (!refined && !l.tag && l.qty >= 3) {
          r.appendChild(this.button(`Refine all (${Math.floor(l.qty * lineEff)} out)`, true, () =>
            conn.reducers.spaceRefine({ lotId: l.lotId, qty: l.qty, structId: 0n })));
        }
        body.appendChild(r);
      }
      // space campaign J (report §17.4): the FOUNDRY — one component process with profiles, timed jobs, exact cancellation.
      renderFoundry(this.host(), body, { me, sov, activeShip: activeShip ? { shipId: activeShip.shipId, chassisKey: activeShip.chassisKey, name: activeShip.name } : null });
    } else if (this.tab === 'station') {
      // space campaign K (report §8.3, §19.6, §20.1): what this station is for, its quoted services, the firms' plans.
      renderStation(this.host(), body, { me, sov, activeShip: activeShip ? { shipId: activeShip.shipId, chassisKey: activeShip.chassisKey, name: activeShip.name } : null });
    } else if (this.tab === 'compare') {
      // space campaign J (report §17.1, §17.5): here LIVE against there OBSERVED; the four separate actions.
      renderMarkets(this.host(), body, { me, sov, activeShip: activeShip ? { shipId: activeShip.shipId, chassisKey: activeShip.chassisKey, name: activeShip.name } : null });
    } else if (this.tab === 'health') {
      // space campaign J (report §17.5, §17.6): the yard and port cards in the server's own numbers.
      renderEconomy(this.host(), body);
    } else if (this.tab === 'hold') {
      // ── THE HOLD LAW + THE WAREHOUSE LAW (Space Wave 2): the active hull's
      // hold (cargo × 25 u) and your warehouse AT THIS STATION; Load / Unload
      // move goods between them; consigned crates stay aboard.
      const { stationKey: hereKey } = this.stationCtx();
      const active = [...conn.db.playerShip.iter()].find((s) => s.owner.isEqual(me) && s.active) ?? null;
      let cargoQ = 0;
      if (active) for (const c of conn.db.shipComponent.iter()) if (c.shipId === active.shipId && c.slot === 'cargo') cargoQ = c.quality;
      const cap = active ? ((CHASSIS[active.chassisKey]?.cargo ?? 4) + Math.round(cargoQ / 1000 * 20)) * HOLD_UNITS_PER_CARGO : 0;
      const mine = (() => { try { return [...conn.db.cargoLot.iter()].filter((l) => l.owner.isEqual(me)); } catch { return []; } })();
      const hold = active ? mine.filter((l) => l.locKind === 'ship' && l.locId === `ship:${active.shipId}`) : [];
      const here = mine.filter((l) => l.locKind === 'station' && l.locId === hereKey);
      const used = hold.reduce((n, l) => n + l.qty, 0);
      const head = document.createElement('div');
      head.style.cssText = 'color:var(--ax-soft);font:11px var(--ax-mono)';
      head.textContent = `${active?.name ?? 'no hull'} — hold ${used}/${cap} u · warehouse at ${hereKey}: ${here.reduce((n, l) => n + l.qty, 0)} u`;
      body.appendChild(head);
      const bar = document.createElement('div');
      bar.style.cssText = 'height:6px;background:var(--ax-bg3);border:1px solid var(--ax-line2);border-radius:var(--ax-radius);overflow:hidden';
      const fill = document.createElement('div');
      fill.style.cssText = `height:100%;width:${cap ? Math.min(100, used / cap * 100) : 0}%;background:${used >= cap ? 'var(--ax-bad)' : 'var(--ax-accent)'}`;
      bar.appendChild(fill);
      body.appendChild(bar);
      const sec = (label: string): void => {
        const h = document.createElement('div');
        h.style.cssText = 'color:var(--ax-muted);font:700 10px var(--ax-mono);letter-spacing:1px;margin-top:6px';
        h.textContent = label;
        body.appendChild(h);
      };
      sec('ABOARD');
      if (!hold.length) { const d = document.createElement('div'); d.style.cssText = 'color:var(--ax-muted)'; d.textContent = 'The hold is empty.'; body.appendChild(d); }
      for (const l of hold) {
        const r = this.row();
        const d = document.createElement('div');
        d.style.cssText = 'flex:1';
        d.textContent = `${l.name} · ${l.qty} u${l.tag ? ' · consigned (rides to its destination)' : ''}`;
        r.appendChild(d);
        r.appendChild(this.button('Unload', !l.tag, () => conn.reducers.unloadCargo({ lotId: l.lotId, qty: l.qty })));
        body.appendChild(r);
      }
      sec('IN THE WAREHOUSE HERE');
      if (!here.length) { const d = document.createElement('div'); d.style.cssText = 'color:var(--ax-muted)'; d.textContent = 'Nothing stored at this station.'; body.appendChild(d); }
      for (const l of here) {
        const r = this.row();
        const d = document.createElement('div');
        d.style.cssText = 'flex:1';
        d.textContent = `${l.name} · ${l.qty} u`;
        r.appendChild(d);
        r.appendChild(this.button('Load', used < cap, () => conn.reducers.loadCargo({ lotId: l.lotId, qty: l.qty })));
        body.appendChild(r);
      }
    } else {
      // ── Duty: contracts + the faction arc (versona-civ career) ──
      // Loyalty (tier-2): claimed contracts bank LP; the store trades it.
      try {
        interface LpRow { identity: { isEqual(o: unknown): boolean }; factionKey: string; lp: bigint }
        const ldb = conn.db as unknown as { pilotLoyalty: { iter(): Iterable<LpRow> } };
        const lpRow = [...ldb.pilotLoyalty.iter()].find((l) => l.identity.isEqual(me));
        const lp = lpRow?.lp ?? 0n;
        const R6 = conn.reducers as unknown as { redeemLoyalty(a2: { offerKey: string }): Promise<void> };
        const lpHead = document.createElement('div');
        lpHead.style.cssText = 'color:var(--ax-hi);font:11px var(--ax-mono);margin-bottom:2px';
        lpHead.textContent = `Loyalty: ${lp} LP${lpRow ? ` (${lpRow.factionKey})` : ''} — claimed contracts +25, war work +10`;
        body.appendChild(lpHead);
        for (const [key, cost, label] of [['crate_faction', 50n, 'Faction weapon (q700)'], ['crate_engine', 90n, 'Faction engine (q780)'], ['crate_elite', 150n, 'Elite shield emitter (q850)']] as Array<[string, bigint, string]>) {
          const r = this.row();
          const d = document.createElement('div');
          d.style.cssText = 'flex:1;font:11px var(--ax-mono)';
          d.textContent = `${label} — ${cost} LP`;
          r.append(d, this.button('Redeem', lp >= cost, () => R6.redeemLoyalty({ offerKey: key })));
          body.appendChild(r);
        }
      } catch { /* pre-publish */ }
      const officer = document.createElement('div');
      officer.textContent = `${FACTION_OFFICER[faction] ?? FACTION_OFFICER['']} has work for a pilot.`;
      officer.style.cssText = 'color:var(--ax-soft);font:11px var(--ax-mono)';
      body.appendChild(officer);
      if (!faction) {
        const r = this.row();
        const d = document.createElement('div');
        d.style.cssText = 'flex:1';
        d.textContent = 'Swear to a civ for the +20% allegiance dividend and the career arc:';
        r.append(d,
          this.button('Aurelian Concord', true, () => conn.reducers.setPilotFaction({ factionKey: 'aurelian' })),
          this.button('Borean Compact', true, () => conn.reducers.setPilotFaction({ factionKey: 'borean' })));
        body.appendChild(r);
      }
      for (const m of missions) {
        const r = this.row(m.state === 'done' ? 'border-color:var(--ax-accent)' : '');
        const d = document.createElement('div');
        d.style.cssText = 'flex:1';
        d.textContent = `${m.kind.toUpperCase()} — den poi${m.targetPoi} · ${m.progress}/${m.goal}${m.state === 'done' ? ' · COMPLETE' : ''}`;
        r.appendChild(d);
        if (m.state === 'done') {
          r.appendChild(this.button(`Claim ${m.rewardSov} ⓒ${faction ? ' +20%' : ''}`, true, () =>
            conn.reducers.claimDutyMission({ missionId: m.missionId })));
        } else {
          r.appendChild(this.button('Abandon', true, () => conn.reducers.abandonDutyMission({ missionId: m.missionId })));
        }
        body.appendChild(r);
      }
      // The agent ladder: higher boards open with rank (and, for the elite
      // tier, P7 Trustworthiness) — pay also scales +15%/rank at claim.
      let trust = 50;
      try {
        const rep = conn.db.reputation.identity.find(me);
        if (rep) trust = rep.trustworthiness;
      } catch { /* fine */ }
      for (const dk of DUTY_KINDS) {
        if (missions.some((m) => m.kind === dk.kind)) continue;
        const lockedRank = (dk.requiresRank ?? 0) > rank;
        const lockedTrust = (dk.requiresTrust ?? 0) > trust;
        const locked = lockedRank || lockedTrust;
        const r = this.row(locked ? 'opacity:.55' : '');
        const d = document.createElement('div');
        d.style.cssText = 'flex:1';
        const payPct = rank * 15 + (faction ? 20 : 0);
        d.textContent = `${dk.label} — ${dk.brief} · ${dk.rewardSov} ⓒ${payPct ? ` (+${payPct}% standing pay)` : ''}`
          + (locked ? ` · LOCKED: ${lockedRank ? `rank ${dk.requiresRank}+` : ''}${lockedRank && lockedTrust ? ' · ' : ''}${lockedTrust ? `trust ${dk.requiresTrust}+` : ''}` : '');
        r.append(d, this.button('Take', !locked, () => conn.reducers.takeDutyMission({ kind: dk.kind })));
        body.appendChild(r);
      }
      if (faction) {
        const titles = ARC_TITLES[faction] ?? [];
        const arcT = document.createElement('div');
        arcT.textContent = 'CAREER ARC';
        arcT.style.cssText = 'font:700 10px var(--ax-mono);color:var(--ax-muted);letter-spacing:1.2px;margin-top:4px';
        body.appendChild(arcT);
        const r = this.row();
        const d = document.createElement('div');
        d.style.cssText = 'flex:1';
        d.textContent = arcStep >= titles.length
          ? `Arc complete — ${titles[titles.length - 1]}`
          : `Next: ${titles[arcStep]} (step ${arcStep + 1}/${titles.length})`;
        r.appendChild(d);
        if (arcStep < titles.length) {
          r.appendChild(this.button('Advance', true, () => {
            this.status = 'Advancing the arc…';
            conn.reducers.advanceFactionArc({}).then(
              () => { this.status = ''; this.render(); },
              (e: Error) => { this.status = String(e.message ?? e); this.render(); },
            );
          }));
        }
        body.appendChild(r);
      }
      const cr = this.row();
      const cd = document.createElement('div');
      cd.style.cssText = 'flex:1';
      cd.textContent = `Battle tokens: ${tokens} — 3 open a component crate (q400-800)`;
      cr.append(cd, this.button('Open crate', tokens >= 3n, () => conn.reducers.redeemFleetTokens({})));
      body.appendChild(cr);
    }
  }
}
